import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import {
  answerPhotoCheck,
  cookbookError,
  cookbookFileUrl,
  getCookbookRender,
  getRecipeOptions,
  renderCookbook,
  saveCookbook,
} from '../../../../api/cookbook'
import { SpinnerPreview } from '../../../../components/SvgIcon'
import CustomTitle from '../CustomTitle'
import PriceBox from '../PriceBox'
import Footer from '../Footer'
import { STEPS_ORDER_BOOK } from '../../../../utils/constants'
import { cookbookSize } from '../../../../utils/cookbookSizes'
import routes from '../../../../routers'

import styles from './PreviewBook.module.css'

const POLL_MS = 2500
const BUSY = ['queued', 'rendering']
const PRODUCT_NAMES = {
  coil: 'Kitchen Edition (coil bound)',
  hardcover: 'Heirloom Edition (hardcover)',
}

// The open book as a reader sees it: cover, page 1 alone on the right, then spreads
const toSpreads = (render) => {
  if (!render?.pages?.length) return []
  const pages = render.pages.map(cookbookFileUrl)
  const spreads = [
    { cover: cookbookFileUrl(render.cover) },
    { left: null, right: pages[0], first: 1 },
  ]
  for (let i = 1; i < pages.length; i += 2) {
    spreads.push({ left: pages[i], right: pages[i + 1] || null, first: i + 1 })
  }
  return spreads
}

const spreadLabel = (spread, total) => {
  if (spread.cover) return 'Cover: back, spine and front'
  if (!spread.left) return `Page 1 of ${total}`
  return spread.right
    ? `Pages ${spread.first} and ${spread.first + 1} of ${total}`
    : `Page ${spread.first} of ${total}`
}

const CARD_CHOICES = [
  ['typed', 'Typed only'],
  ['typed_card', 'Typed + the original card'],
  ['card', 'The card as the page'],
]

// Recipes with photos of their card or a voice memo: how each prints
const CardOptions = ({ orderId, onChanged, disabled }) => {
  const [recipes, setRecipes] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getRecipeOptions(orderId)
      .then(({ data }) => setRecipes(data?.enabled ? data.recipes : []))
      .catch(() => setRecipes([]))
  }, [orderId])

  if (!recipes.length) return null

  const change = (recipe, patch) => {
    const next = { ...recipe, ...patch }
    setRecipes(recipes.map((r) => (r.recipe_id === recipe.recipe_id ? next : r)))
    setSaving(true)
    saveCookbook({
      id: orderId,
      recipe_options: { [recipe.recipe_id]: { card: next.card, qr: next.qr } },
    })
      .then(onChanged)
      .catch(() => null)
      .finally(() => setSaving(false))
  }

  return (
    <section className="cbSection">
      <CustomTitle
        title="Original cards and voice memos"
        hoverText="Show the handwritten card next to the typed recipe, or print the card itself. Recipes with a voice memo get a small QR code that plays it."
      />
      <div className={styles.cardOptions}>
        {recipes.map((r) => (
          <div key={r.recipe_id} className={styles.cardRow}>
            <div className={styles.cardName}>
              <b>{r.name}</b>
              <small>
                {[
                  r.card_count ? `${r.card_count} card photo${r.card_count === 1 ? '' : 's'}` : '',
                  r.has_voice_memo ? 'voice memo' : '',
                ]
                  .filter(Boolean)
                  .join(', ')}
              </small>
            </div>
            {r.card_count ? (
              <select
                className="cbInput"
                value={r.card}
                disabled={disabled || saving}
                onChange={(e) => change(r, { card: e.target.value })}
                aria-label={`How ${r.name} prints`}
              >
                {CARD_CHOICES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            ) : (
              <span />
            )}
            {r.has_voice_memo ? (
              <label className={styles.qrChoice}>
                <input
                  type="checkbox"
                  checked={r.qr && !r.voice_link_off}
                  disabled={disabled || saving || r.voice_link_off}
                  onChange={(e) => change(r, { qr: e.target.checked })}
                />
                {r.voice_link_off
                  ? 'Voice memo link turned off by its owner'
                  : 'QR code for the voice memo'}
              </label>
            ) : (
              <span />
            )}
          </div>
        ))}
      </div>
      <p className="cbMuted">Changes rebuild the preview.</p>
    </section>
  )
}

// Photos that will likely print pixelated: each must be kept or removed before ordering,
// and every answer is saved with the message shown. Smaller "may look soft" ones are a note.
const PhotoChecks = ({ orderId, checks, onAnswered, disabled }) => {
  const [busyKey, setBusyKey] = useState('')
  const [error, setError] = useState('')
  const low = checks.filter((c) => c.level === 'low')
  const soft = checks.filter((c) => c.level !== 'low')
  if (!checks.length) return null

  const answer = (check, decision) => {
    setBusyKey(check.key)
    setError('')
    answerPhotoCheck(orderId, check.key, decision)
      .then(({ data }) => onAnswered(data))
      .catch((e) => setError(cookbookError(e, 'That didn’t save. Please try again.')))
      .finally(() => setBusyKey(''))
  }

  const waiting = low.filter((c) => !c.kept).length

  return (
    <div className={styles.photoChecks}>
      {low.length ? (
        <>
          <p className={styles.warnTitle}>
            {waiting
              ? `${waiting} photo${waiting === 1 ? '' : 's'} will likely print pixelated`
              : 'Photo quality: all checked'}
          </p>
          {waiting ? (
            <p className="cbHint">
              These photos are too small for a printed page. Keep each one as it is, or take it out
              of the book. A clearer copy of the photo on the recipe prints better.
            </p>
          ) : null}
          <ul className={styles.photoList}>
            {low.map((c) => (
              <li key={c.key} className={styles.photoRow}>
                <img className={styles.photoThumb} src={c.photo_url} alt="" />
                <div className={styles.photoText}>
                  <p>{c.message}</p>
                  {c.kept ? (
                    <p className={styles.photoKept}>
                      You chose to keep it
                      {c.kept_at ? ` on ${new Date(c.kept_at).toLocaleDateString()}` : ''}.
                    </p>
                  ) : (
                    <div className={styles.photoButtons}>
                      <button
                        type="button"
                        className="cbSecondaryButton"
                        disabled={disabled || !!busyKey}
                        onClick={() => answer(c, 'keep')}
                      >
                        {c.keep_answer}
                      </button>
                      <button
                        type="button"
                        className="cbSecondaryButton"
                        disabled={disabled || !!busyKey}
                        onClick={() => answer(c, 'remove')}
                      >
                        {c.remove_answer}
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {soft.length ? (
        <>
          <p className={styles.warnTitle}>May look a little soft</p>
          <ul>
            {soft.map((c) => (
              <li key={c.key}>{c.message}</li>
            ))}
          </ul>
        </>
      ) : null}
      {error ? (
        <p className="cbActionError" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

const PreviewBook = ({ orderUrlData }) => {
  const [render, setRender] = useState(null)
  const [error, setError] = useState('')
  const [index, setIndex] = useState(0)
  const timer = useRef(null)
  const { orderId } = orderUrlData
  const selfServe = useSelector((state) => !!state.user?.data?.cookbook_self_serve)

  const spreads = useMemo(() => toSpreads(render), [render])
  const busy = !error && (!render || BUSY.includes(render.status))
  const spread = spreads[index]
  const navigate = useNavigate()
  const photosWaiting = (render?.photo_checks || []).some((c) => c.level === 'low' && !c.kept)
  const canOrder = render?.status === 'ready' && render?.prices_available && !photosWaiting
  const size = cookbookSize(render?.trim_size)

  const poll = () => {
    timer.current = setTimeout(() => {
      getCookbookRender(orderId)
        .then(({ data }) => {
          setRender(data)
          if (BUSY.includes(data.status)) poll()
          if (data.status === 'failed') setError(data.error)
        })
        .catch(() => setError('We lost touch with the server. Please reload the page.'))
    }, POLL_MS)
  }

  const build = () => {
    clearTimeout(timer.current)
    setError('')
    renderCookbook(orderId)
      .then(({ data }) => {
        setRender(data)
        if (BUSY.includes(data.status)) poll()
        if (data.status === 'failed') setError(data.error)
      })
      .catch((e) => setError(cookbookError(e, 'Something went wrong. Please try again.')))
  }

  // After "Keep" or "Remove": a removal rebuilds the book, so keep checking until it's ready
  const answered = (data) => {
    clearTimeout(timer.current)
    setRender(data)
    if (BUSY.includes(data.status)) poll()
    if (data.status === 'failed') setError(data.error)
  }

  useEffect(() => {
    build()
    return () => clearTimeout(timer.current)
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1))
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(spreads.length - 1, i + 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [spreads.length])

  return (
    <div className="cbStep">
      <section className="cbSection">
        <CustomTitle
          title="Flip through your cookbook"
          hoverText={
            render?.status === 'ready'
              ? `This is exactly what we’ll print: ${render.page_count} pages, ${
                  PRODUCT_NAMES[render.product] || ''
                }, ${size.label}. Use the arrows or your keyboard’s arrow keys.`
              : 'We build every page of your book so you can check it before you order.'
          }
        />

        {error ? (
          <div className={styles.errorBox} role="alert">
            <p className={styles.errorTitle}>We couldn’t build this cookbook yet</p>
            <p className={styles.errorText}>{error}</p>
            <p className="cbHint">Go back to change your recipes or details, then preview again.</p>
          </div>
        ) : null}

        {busy ? (
          <div className={styles.stage}>
            <div className={styles.building}>
              <SpinnerPreview />
              <p className={styles.buildingTitle}>Laying out your pages…</p>
              <p className="cbHint">This usually takes under a minute. Hold tight!</p>
            </div>
          </div>
        ) : null}

        {spread ? (
          <div className={styles.stage}>
            <div className={styles.viewer}>
              <button
                type="button"
                aria-label="Previous pages"
                className={styles.arrow}
                disabled={index === 0}
                onClick={() => setIndex(index - 1)}
              >
                <span className={`${styles.arrowIcon} ${styles.arrowLeft}`} />
              </button>
              <div
                className={`${styles.book} ${spread.cover ? styles.bookCover : ''}`}
                style={{ '--page-ratio': size.ratio }}
              >
                {spread.cover ? (
                  <img src={spread.cover} alt="Cookbook cover: back, spine and front" />
                ) : (
                  <>
                    {spread.left ? (
                      <img src={spread.left} alt={`Page ${spread.first}`} />
                    ) : (
                      <div className={styles.blankPage} />
                    )}
                    {spread.right ? (
                      <img
                        src={spread.right}
                        alt={`Page ${spread.left ? spread.first + 1 : spread.first}`}
                      />
                    ) : (
                      <div className={styles.blankPage} />
                    )}
                  </>
                )}
              </div>
              <button
                type="button"
                aria-label="Next pages"
                className={styles.arrow}
                disabled={index === spreads.length - 1}
                onClick={() => setIndex(index + 1)}
              >
                <span className={styles.arrowIcon} />
              </button>
            </div>
            <div className={styles.scrubber}>
              <span className={styles.where}>{spreadLabel(spread, render.page_count)}</span>
              {spreads.length > 2 ? (
                <input
                  type="range"
                  min={0}
                  max={spreads.length - 1}
                  value={index}
                  aria-label="Jump to a page"
                  onChange={(e) => setIndex(Number(e.target.value))}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        {render?.status === 'ready' && render?.photo_checks?.length ? (
          <PhotoChecks
            orderId={orderId}
            checks={render.photo_checks}
            onAnswered={answered}
            disabled={busy}
          />
        ) : null}

        {render?.status === 'ready' && render?.warnings?.length ? (
          <div className={styles.warnBox} role="status">
            <p className={styles.warnTitle}>Before you order, check these</p>
            <ul>
              {render.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            <p className="cbHint">
              A sharper photo (taken closer, in good light) prints better. You can still order as it
              is.
            </p>
          </div>
        ) : null}

        {render?.status === 'ready' && render?.pdf_locked ? (
          <p className={styles.downloads}>
            <Link className="cbTextLink" to={`${routes.cookbookPdf}/${orderId}`}>
              Download as a PDF
            </Link>{' '}
            (Pro)
          </p>
        ) : null}

        {render?.files ? (
          <p className={styles.downloads}>
            Want a closer look? Download the{' '}
            <a className="cbTextLink" href={cookbookFileUrl(render.files.interior)}>
              inside pages
            </a>{' '}
            or the{' '}
            <a className="cbTextLink" href={cookbookFileUrl(render.files.cover)}>
              cover
            </a>{' '}
            as a PDF.
          </p>
        ) : null}
      </section>

      <CardOptions orderId={orderId} onChanged={build} disabled={busy} />

      {render?.status === 'ready' && render?.prices_available ? (
        <PriceBox orderId={orderId} />
      ) : null}

      <Footer
        backTo={-1}
        onSubmit={(e) => {
          e.preventDefault()
          if (canOrder) {
            navigate('.', {
              state: { ...orderUrlData, currentStep: STEPS_ORDER_BOOK['CHECKOUT_STEP'] },
            })
          }
        }}
        buttonClass={canOrder ? '' : 'cbNextButtonOff'}
        buttonText={
          photosWaiting && render?.status === 'ready'
            ? 'Check the photos above first'
            : canOrder
            ? selfServe
              ? 'Next: shipping & payment'
              : 'Next: shipping & request'
            : busy
            ? 'Building…'
            : 'Ordering coming soon'
        }
        note={
          <span className="cbActionNote">
            Questions?{' '}
            <a className="cbTextLink" href="mailto:info@getmorsel.com">
              Email us
            </a>
          </span>
        }
      />
    </div>
  )
}

export default PreviewBook
